import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../lib/i18n';
import { photoUrl } from '../../lib/photo';
import type { InspectionPhoto } from '../../lib/types';
import { usePhotoLabel } from './labels';

interface Props {
  photos: InspectionPhoto[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  /** Buttons for the photo on screen, shown under it — retake and delete while a walk-round is open. */
  actions?: ((photo: InspectionPhoto) => ReactNode) | undefined;
}

/** How far a finger has to travel sideways before it counts as a swipe. */
const SWIPE_PX = 50;

/**
 * Photos full screen, one at a time: swipe or arrow keys between them, with
 * what each one shows written underneath.
 */
export function PhotoViewer({ photos, index, onIndex, onClose, actions }: Props) {
  const { t } = useI18n();
  const label = usePhotoLabel();
  const start = useRef<{ x: number; y: number } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const photo = photos[index];

  const go = useCallback((step: number) => {
    const next = index + step;
    if (next >= 0 && next < photos.length) onIndex(next);
  }, [index, photos.length, onIndex]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [go, onClose]);

  useEffect(() => setLoaded(false), [photo?.id]);

  if (!photo) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label(photo)}
      className="fixed inset-0 z-[60] flex flex-col bg-black text-white"
    >
      <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <span className="tabular text-sm text-white/70">{index + 1} / {photos.length}</span>
        <a
          href={photoUrl(photo.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-white/70 underline-offset-2 hover:underline"
        >
          {t('inspection.openOriginal')}
        </a>
        <button
          onClick={onClose}
          aria-label={t('inspection.close')}
          className="grid h-11 w-11 place-items-center rounded-xl text-xl text-white/80 transition hover:bg-white/10"
        >
          ✕
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 touch-pan-y items-center justify-center"
        onPointerDown={(event) => { start.current = { x: event.clientX, y: event.clientY }; }}
        onPointerUp={(event) => {
          const from = start.current;
          start.current = null;
          if (!from) return;
          const dx = event.clientX - from.x;
          if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(event.clientY - from.y)) go(dx < 0 ? 1 : -1);
        }}
      >
        {/* The small copy shows at once; the full photo replaces it when it lands. */}
        <img
          key={`thumb-${photo.id}`}
          src={photoUrl(photo.id, 'thumb')}
          alt=""
          aria-hidden
          className={`absolute max-h-full max-w-full object-contain blur-sm transition-opacity ${loaded ? 'opacity-0' : 'opacity-100'}`}
        />
        <img
          key={photo.id}
          src={photoUrl(photo.id)}
          alt={label(photo)}
          onLoad={() => setLoaded(true)}
          draggable={false}
          className="relative max-h-full max-w-full select-none object-contain"
        />

        {index > 0 && (
          <button
            onClick={() => go(-1)}
            aria-label={t('inspection.previous')}
            className="absolute left-2 hidden h-12 w-12 place-items-center rounded-full bg-black/50 text-2xl sm:grid"
          >
            ‹
          </button>
        )}
        {index < photos.length - 1 && (
          <button
            onClick={() => go(1)}
            aria-label={t('inspection.next')}
            className="absolute right-2 hidden h-12 w-12 place-items-center rounded-full bg-black/50 text-2xl sm:grid"
          >
            ›
          </button>
        )}
      </div>

      <div className="space-y-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        <p className="text-sm font-semibold">{label(photo)}</p>
        {photo.note && <p className="text-sm text-white/70">{photo.note}</p>}
        {actions && <div className="flex gap-2 pt-1">{actions(photo)}</div>}
      </div>
    </div>,
    document.body,
  );
}
